import { NotificationManagementSearcher } from '@/components/forms/notification-management/searcher.form';
import { NotificationManagementTable } from '@/components/tables/notification-management/notification-management.table';
import NotificationManagementTabs from '@/components/tabs/notification-management.tabs';
import { Message } from '@/enums/message.enum';
import { NotificationPermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setMessages } from '@/redux/slices/app.slice';
import {
  deleteNotification,
  deleteEmailNotification,
  getListNotification,
  resetMessageNotification,
  watchMessageNotification,
  getFrequencyTimes,
  getLinkFunctions,
  getWeekDays,
  getTargetGroups,
  watchFrequencyTimes,
  watchLinkFunctions,
  watchWeekDays,
  watchTargetGroups
} from '@/redux/slices/notification.slice';
import { PageURLs } from '@/utils/navigate';
import { css, Global } from '@emotion/react';
import { App, Col, Form, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';

export default function NotificationManagement() {
  const { push, type } = useQuery();
  const form = Form.useForm<FilterNotification>();
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<FilterNotification>(
    {} as FilterNotification
  );
  const { modal } = App.useApp();

  const messageInfo = useAppSelector(watchMessageNotification);

  const frequencyTimes = useAppSelector(watchFrequencyTimes);
  const linkFunctions = useAppSelector(watchLinkFunctions);
  const weekDays = useAppSelector(watchWeekDays);
  const targetGroups = useAppSelector(watchTargetGroups);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        form[0].resetFields();
      }
      dispatch(resetMessageNotification());
    }
  }, [dispatch, messageInfo, form]);

  const modalConfirmDelete = (id: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá thông báo này không?',
      content: <em>Khi nhấn xóa thông báo này thì toàn bộ dữ liệu đã tạo sẽ bị xóa.</em>,
      okText: 'Xoá',
      cancelText: 'Hủy',
      onOk: () => handleDelete(id)
    });
  };

  useEffect(() => {
    if (frequencyTimes.length === 0) {
      dispatch(getFrequencyTimes());
    }
    if (linkFunctions.length === 0) {
      dispatch(getLinkFunctions());
    }
    if (weekDays.length === 0) {
      dispatch(getWeekDays());
    }
    if (targetGroups.length === 0) {
      dispatch(getTargetGroups());
    }
  }, [
    dispatch,
    frequencyTimes.length,
    linkFunctions.length,
    weekDays.length,
    targetGroups.length
  ]);

  useEffect(() => {
    dispatch(
      getListNotification({
        filter: { TYPE: type === 'EMAIL' ? 'EMAIL' : 'WEB/APP' }
      })
    );
  }, [dispatch, type]);

  const handleSearch = async (values: FilterNotification) => {
    try {
      setFilter(values);
      dispatch(
        getListNotification({
          filter: {
            ...values
          },
          currentPage: 1,
          limitPage: 10
        })
      );
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleEdit = (id: number) => {
    push(
      `${PageURLs.ofNotificationManagement()}?mode=${type === 'EMAIL' ? NotificationPermissionMode.EDITEMAIL : NotificationPermissionMode.EDITWEBAPP}&id=${id}`
    );
  };

  const handleDelete = (id: number) => {
    if (type === 'EMAIL') {
      dispatch(deleteEmailNotification(id));
    } else {
      dispatch(deleteNotification(id));
    }
  };

  return (
    <>
      <Global
        styles={css`
          .ant-modal-confirm-btns {
            display: flex !important;
            justify-content: center !important;
            padding-top: 2rem;
          }
        `}
      />
      <div css={rootStyles}>
        <Row css={boxStyles} justify='space-between' align='middle'>
          <Col>
            <Typography.Title css={titleStyles} level={5}>
              QUẢN LÝ THÔNG BÁO
            </Typography.Title>
          </Col>
        </Row>
        <NotificationManagementTabs
          activeKey={type && type === 'EMAIL' ? 'EMAIL' : 'WEBAPP'}
          onChange={(key) =>
            push(`${PageURLs.ofNotificationManagement()}?type=${key}`)
          }
        />
  
        <NotificationManagementSearcher
          form={form[0]}
          type={type === 'EMAIL' ? 'EMAIL' : 'WEBAPP'}
          onSearch={handleSearch}
        />
  
        <NotificationManagementTable
          type={type === 'EMAIL' ? 'EMAIL' : 'WEBAPP'}
          onEdit={handleEdit}
          onDelete={modalConfirmDelete}
          filter={filter}
        />
      </div>
    </>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;
